import { Config, Picture } from "./config.interface"
import imageManager from "./imageManager";
import Drawer from "./drawer"
import Hammer, { Rotate } from "hammerjs"
import { Observable, Subject } from "rxjs"

export interface Poi { id: string, color: number[] }
export interface Pois { [key: string]: Poi }

interface Frame {
    sd: {
        path: string,
        image: HTMLImageElement | undefined
    },
    hd: {
        path: string,
        image: HTMLImageElement | undefined
    }
}

interface FrameWire {
    sd: {
        path: string,
        image: HTMLImageElement | undefined
    },
    hd: {
        path: string,
        image: HTMLImageElement | undefined
    }
}

interface FrameMap {
    sd: {
        path: string,
        image: HTMLImageElement | undefined
    },
    hd: {
        path: string,
        image: HTMLImageElement | undefined
    }
}

enum Status {
    NEW,
    FIRST_FRAME_LOADED,
    BLUR_LOADED,
    FULL_LOADED
}

interface Scene {
    wiresId: string[]
    name: string;
    wire: boolean;
    effectMap: boolean;
    frames: Frame[];
    framesWire: FrameWire[];
    framesMap: FrameMap[];
    status: Status;
    groupId: number
}

export const sleep = async (ms?: number) => {
    return new Promise(r => setTimeout(r, ms ? ms : 0))
}

export const pad = (num: number, size: number, reverseSize?: number) => {
    num = reverseSize ? reverseSize - num : num
    const s = "000000000" + num;
    return s.substr(s.length - size);
}

declare var DocumentTouch: any;
export const is_touch_device4 = () => {

    var prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');

    const mq = function (query: any) {
        return window.matchMedia(query).matches;
    }

    if (('ontouchstart' in window) || (window as any).DocumentTouch && document instanceof DocumentTouch) {
        return true;
    }

    // include the 'heartz' as a way to have a non matching MQ to help terminate the join
    // https://git.io/vznFH
    var query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
    return mq(query);
}

export default class {
    container!: HTMLElement;
    imageManager = new imageManager()
    config!: Config;
    drawer: Drawer;
    activeScene!: Scene;
    scenes: Scene[] = [];
    angle = 0;
    divContainer = document.createElement("div")
    glCanvas = document.createElement("canvas")
    mouseCanvas = document.createElement("canvas")
    mouseCtx: CanvasRenderingContext2D;
    pxRatio = window.devicePixelRatio | 1;
    displayWidth = 0;
    displayHeight = 0;
    lastContainerWidth = -1;
    lastContainerHeight = -1;
    overflow = 5
    rotationSensitivity = 1;
    wireBaseBlur = 1;
    wireData?: Uint8ClampedArray;
    hammer!: HammerManager;
    activeFrame!: Frame;
    activeFrameWire!: FrameWire;
    activeFrameMap!: FrameMap;
    lastActiveFrame!: Frame;
    pois: Pois = {};
    isTUrning = false;
    isTouch = is_touch_device4()
    loadImageIndex = 0;
    isColorCHanged = false;
    imageWidth = 1920;
    imageHeight = 1080;
    wireOffset = 0;
    loadMode = "slow"
    events = {
        mouseMove: new Subject<{ poi: Poi | undefined, clientX: number, clientY: number }>(),
        tap: new Subject<{ poi: Poi | undefined, clientX: number, clientY: number }>(),
        loading: new Subject<boolean>(),
        fastLoading: new Subject<number[]>(),
        resize: new Subject<{ totalWidth: number, width: number, height: number }>(),
        startRotating: new Subject(),
        rotate: new Subject<number>()
    }
    constructor(conf: { width: number, height: number, loadMode: string }) {
        this.loadMode = conf.loadMode
        this.imageHeight = conf.height
        this.imageWidth = conf.width
        this.drawer = new Drawer(this.glCanvas)
        this.mouseCtx = this.mouseCanvas.getContext("2d") as CanvasRenderingContext2D;
        this.listenResize()
        this.startTick()
    }

    public applyColorPoi() {
        this.isColorCHanged = true;
    }

    private async startTick() {
        while (true) {
            try {
                if (this.lastActiveFrame !== this.activeFrame) {
                    if (this.activeScene.status >= Status.BLUR_LOADED) {
                        if (!this.drawer.lastPicDrawed || this.drawer.lastPicDrawed.image !== this.activeFrame.hd.image) {
                            // PAINT
                            if (this.activeScene.status !== Status.FULL_LOADED) {
                                this.glCanvas.style.filter = `blur(${Math.floor(6)}px)`;
                            } else {
                                this.glCanvas.style.filter = `blur(${Math.floor(0)}px)`;
                            }
                            this.drawer.paint(
                                this.activeFrame[this.activeScene.status === Status.FULL_LOADED ? "hd" : "sd"].image as HTMLImageElement,
                                this.activeFrameWire[this.activeScene.status === Status.FULL_LOADED ? "hd" : "sd"].image as HTMLImageElement,
                                this.activeFrameMap[this.activeScene.status === Status.FULL_LOADED ? "hd" : "sd"].image as HTMLImageElement
                            )
                        }
                    }
                    this.lastActiveFrame = this.activeFrame;
                }
                if (this.activeScene) {
                    if (this.isColorCHanged) {
                        this.drawer.setColors(this.activeScene.wiresId.map(e => this.pois[e].color))
                        this.isColorCHanged = false;
                    }
                }

            } catch (error) {
                console.error(error);
            }
            await new Promise(r => requestAnimationFrame(r))
        }
    }


    public setConfig(config: Config) {
        this.config = config;
        const pois: Pois = {}
        for (let pic of config.pictures) {
            if (pic.wireColors) {
                for (let wire of pic.wireColors) {
                    pois[wire.name] = { id: wire.name, color: Array.from({ length: 4 }).map(e => Math.random()) }
                }
            }
            this.scenes.push({
                name: pic.name,
                wire: pic.wire,
                groupId: pic.groupId,
                wiresId: pic.wireColors ? pic.wireColors.map(e => e.name) : [],
                status: Status.NEW,
                effectMap: pic.effectMap,
                frames: Array.from({ length: pic.length }).map((e, i) => {
                    return {
                        hd: {
                            path: `/pictures/${pic.name}/hd/pic/${pad(i, 4)}.jpg`,
                            image: undefined
                        },
                        sd: {
                            path: `/pictures/${pic.name}/sd/pic/${pad(i, 4)}.jpg`,
                            image: undefined
                        }
                    }
                }),

                framesWire: Array.from({ length: Math.ceil(pic.length / 3) }).map((e, i) => ({
                    hd: {
                        path: `/pictures/${pic.name}/hd/wire/${pad(i * 3, 4)}.png`,
                        image: undefined
                    },
                    sd: {
                        path: `/pictures/${pic.name}/sd/wire/${pad(i * 3, 4)}.png`,
                        image: undefined
                    }
                })),

                framesMap: Array.from({ length: Math.ceil(pic.length / 3) }).map((e, i) => ({
                    hd: {
                        path: `/pictures/${pic.name}/hd/map/${pad(i * 3, 4)}.jpg`,
                        image: undefined
                    },
                    sd: {
                        path: `/pictures/${pic.name}/sd/map/${pad(i * 3, 4)}.jpg`,
                        image: undefined
                    }
                }))

            })
        }
        this.pois = pois;
        return pois;
    }

    private async listenResize() {
        while (true) {
            if (this.container && this.config && (this.container.clientWidth !== this.lastContainerWidth || this.container.clientHeight !== this.lastContainerHeight)) {
                this.lastContainerWidth = this.container.clientWidth;
                this.lastContainerHeight = this.container.clientHeight;
                this.handleSize()
            }
            await sleep(500);
        }
    }

    public handleSize() {
        if (this.displayWidth !== this.container.clientWidth) {
            // this.pxRatio = window.devicePixelRatio | 1;
            this.pxRatio = 1;
            this.displayWidth = this.container.clientWidth
            this.displayHeight = this.displayWidth * (this.config.height / this.config.width)
            if (this.displayHeight > this.container.clientHeight) {
                this.displayHeight = this.container.clientHeight
                this.displayWidth = this.container.clientHeight * (this.config.width / this.config.height)
            }
            this.divContainer.style.width = `${this.displayWidth}px`;
            this.divContainer.style.height = `${this.displayHeight}px`;

            this.glCanvas.width = this.imageWidth;
            this.glCanvas.height = this.imageHeight;

            // this.glCanvas.width = this.displayWidth * this.pxRatio;
            // this.glCanvas.height = this.displayHeight * this.pxRatio;

            this.glCanvas.style.width = `${this.displayWidth + this.overflow * 2}px`;
            this.glCanvas.style.height = `${this.displayHeight + this.overflow * 2}px`;
            this.glCanvas.style.top = `${-this.overflow}px`;
            this.glCanvas.style.left = `${-this.overflow}px`;
            if (this.activeScene) {
                this.setHdFrame()
            }
            this.drawer.updateGlSize()
            this.events.resize.next({ totalWidth: this.container.clientWidth, width: this.displayWidth, height: this.displayHeight })
        }
    }

    private getActualFrame() {
        return this.activeScene.frames[Math.floor(this.angle / 360 * this.activeScene.frames.length)];
    }

    private getWireOffset() {
        return Math.abs(2 - Math.floor(this.angle / 360 * this.activeScene.frames.length) % 3)
    }

    private getWireIndex(frameIndex: number) {
        return Math.floor(frameIndex / 3);
    }

    private getMapIndex(frameIndex: number) {
        return Math.floor(frameIndex / 3);
    }

    private getMapOffset() {
        return Math.abs(2 - Math.floor(this.angle / 360 * this.activeScene.frames.length) % 3);
    }

    private getActualMap() {
        return this.activeScene.framesMap[Math.floor(Math.floor(this.angle / 360 * this.activeScene.frames.length) / 3)];
    }


    private getActualWire() {
        return this.activeScene.framesWire[Math.floor(Math.floor(this.angle / 360 * this.activeScene.frames.length) / 3)];
    }


    private setAngle(angle: number) {
        angle = angle % 360;
        if (angle > 360) {
            angle += -360;
        } else if (angle < 0) {
            angle += 360
        }
        this.events.rotate.next(angle)
        this.angle = angle;
        this.activeFrame = this.getActualFrame()
        this.activeFrameWire = this.getActualWire()
        this.activeFrameMap = this.getActualMap()
        this.wireOffset = this.getWireOffset()
        this.drawer.setWireOffset(this.wireOffset)
    }

    private getPoiFromPos(xRatio: number, yRatio: number) {
        if (xRatio < 1 && yRatio < 1) {
            const data = this.mouseCtx.getImageData(this.mouseCanvas.width * xRatio, this.mouseCanvas.height * yRatio, 1, 1).data
            let offset = 0;
            if (this.wireOffset === 0) offset = 2
            if (this.wireOffset === 1) offset = 1
            if (this.wireOffset === 2) offset = 0

            if (data[offset] !== 255) {
                const res = this.activeScene.wiresId[data[offset]]
                return res ? res : false;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    private handleTouch() {
        this.hammer = new Hammer(this.glCanvas)
        let startAngle = 0;
        let lastPoiOvered: string | undefined;
        let panStarted = false;
        this.hammer.on('panstart', (e) => {
        })

        this.hammer.on('tap', (e) => {
            const poi = this.getPoiFromPos((e.srcEvent as any).offsetX / this.displayWidth, (e.srcEvent as any).offsetY / this.displayHeight)
            this.events.tap.next({ poi: poi ? this.pois[poi] : undefined, clientX: e.center.x, clientY: e.center.y })
        })

        this.hammer.on('panmove', (e) => {
            if (e.direction === 2 || e.direction == 4) {
                if (!panStarted) {
                    if (this.activeScene.status >= Status.BLUR_LOADED) {
                        this.glCanvas.style.transition = `0s filter linear`
                        // this.glCanvas.style.filter = `blur(${Math.floor(this.activeScene.status !== Status.FULL_LOADED ? 5 : 0)}px)`;
                        startAngle = this.angle
                        this.isTUrning = true;
                        panStarted = true;
                        this.events.startRotating.next()
                    }
                }
                if (this.activeScene.status >= Status.BLUR_LOADED) {
                    this.setAngle(startAngle + ((e.deltaX / this.glCanvas.clientWidth) * 360) / this.rotationSensitivity);
                }
            }
        })

        this.hammer.on('panend', async (e) => {
            panStarted = false;
            const scene = this.activeScene
            await this.setHdFrame()
            // this.glCanvas.style.transition = `0.1s filter linear`
            this.isTUrning = false;
            if (scene === this.activeScene) {
                if (this.loadMode === "slow") this.loadImagesSlow(scene)
            }
        })

        if (!this.isTouch) {
            this.glCanvas.addEventListener("mousemove", (e) => {
                if (this.activeScene && this.activeScene.wire && !this.isTUrning) {
                    const poi = this.getPoiFromPos(e.offsetX / this.displayWidth, e.offsetY / this.displayHeight)
                    this.events.mouseMove.next({ poi: poi ? this.pois[poi] : undefined, clientX: e.clientX, clientY: e.clientY })
                }
            })
        }
    }


    public setContainer(container: HTMLElement) {
        this.container = container;
        this.mouseCanvas.style.visibility = "hidden"
        this.mouseCanvas.className = "mouseCanvas"

        this.divContainer.style.overflow = "hidden"
        this.divContainer.style.position = "relative"
        this.divContainer.style.margin = "auto"
        this.glCanvas.style.position = "absolute"

        this.container.appendChild(this.divContainer)
        this.divContainer.appendChild(this.glCanvas)
        this.divContainer.appendChild(this.mouseCanvas)
        this.handleTouch()
    }

    private async loadImagesSlow(targetScene: Scene) {
        this.loadImageIndex += 1;
        const lastLoadImageIndex = this.loadImageIndex
        // Active scene blur
        if (lastLoadImageIndex === this.loadImageIndex && this.activeScene.status < Status.BLUR_LOADED) {
            const res = await this.imageManager.loadImages(
                this.activeScene.frames.map(e => e.sd.path)
                    .concat(this.activeScene.wire ? this.activeScene.framesWire.map(e => e.sd.path) : [])
                    .concat(this.activeScene.effectMap ? this.activeScene.framesMap.map(e => e.sd.path) : [])
            )
            if (res) {
                targetScene.frames.forEach(frame => {
                    frame.sd.image = this.imageManager.images[frame.sd.path]
                })
                if (targetScene.wire) {
                    targetScene.framesWire.forEach(frameWire => {
                        frameWire.sd.image = this.imageManager.images[frameWire.sd.path]
                    })
                }
                if (targetScene.effectMap) {
                    targetScene.framesMap.forEach(frameMap => {
                        frameMap.sd.image = this.imageManager.images[frameMap.sd.path]
                    })
                }

                targetScene.status = Status.BLUR_LOADED
                this.events.loading.next(false);
            }
        }

        (async () => {
            while (lastLoadImageIndex === this.loadImageIndex) {
                for (let pic of this.activeScene.frames) {
                    if (this.imageManager.images[pic.hd.path]) {
                        if (!this.drawer.gl.imageTexurePicCache.find(img => img === this.imageManager.images[pic.hd.path])) {
                            this.drawer.preloadImage(this.imageManager.images[pic.hd.path], false, false)
                            await sleep(0)
                        }
                    }
                }
                if (this.activeScene.wire) {
                    for (let frameWire of this.activeScene.framesWire) {
                        if (this.imageManager.images[frameWire.hd.path]) {
                            if (!this.drawer.gl.imageTexureWireCache.find(img => img === this.imageManager.images[frameWire.hd.path])) {
                                this.drawer.preloadImage(this.imageManager.images[frameWire.hd.path], true, false)
                            }
                        }
                    }
                }
                if (this.activeScene.effectMap) {
                    for (let frameMap of this.activeScene.framesMap) {
                        if (this.imageManager.images[frameMap.hd.path]) {
                            if (!this.drawer.gl.imageTexureMapCache.find(img => img === this.imageManager.images[frameMap.hd.path])) {
                                this.drawer.preloadImage(this.imageManager.images[frameMap.hd.path], false, true)
                            }
                        }
                    }
                }
                await sleep(0)
            }
        })()

        // Other scene HD actual frame
        if (lastLoadImageIndex === this.loadImageIndex && this.activeScene.status >= Status.BLUR_LOADED) {
            const index = this.activeScene.frames.findIndex(f => f === this.getActualFrame())
            const queue = [];
            for (let scene of this.scenes.filter((scene, i) => (scene !== targetScene && scene.groupId === targetScene.groupId))) {
                // console.log(scene);
                queue.push(scene.frames[index].hd.path)
                if (scene.wire) {
                    queue.push(scene.framesWire[this.getWireIndex(index)].hd.path)
                }
                if (scene.effectMap) {
                    queue.push(scene.framesMap[this.getMapIndex(index)].hd.path)
                }
            }
            await this.imageManager.loadImages(queue)
        }

        // Actual Scene HD frames
        if (lastLoadImageIndex === this.loadImageIndex && this.activeScene.status === Status.BLUR_LOADED) {
            const res = await this.imageManager.loadImages(this.activeScene.frames.map(e => e.hd.path)
                .concat(this.activeScene.wire ? this.activeScene.framesWire.map(e => e.hd.path) : [])
                .concat(this.activeScene.effectMap ? this.activeScene.framesMap.map(e => e.hd.path) : [])
            )
            if (res) {
                targetScene.frames.forEach(frame => {
                    frame.hd.image = this.imageManager.images[frame.hd.path]
                })
                if (targetScene.wire) {
                    targetScene.framesWire.forEach(frameWire => {
                        frameWire.hd.image = this.imageManager.images[frameWire.hd.path]
                    })
                }
                if (targetScene.effectMap) {
                    targetScene.framesMap.forEach(frameMap => {
                        frameMap.hd.image = this.imageManager.images[frameMap.hd.path]
                    })
                }

                targetScene.status = Status.FULL_LOADED
            }
        }

        // Other Scenes BLUR
        for (let scene of this.scenes.filter((scene, i) => scene !== targetScene && scene.status < Status.BLUR_LOADED)) {
            if (lastLoadImageIndex === this.loadImageIndex) {
                const res = await this.imageManager.loadImages(scene.frames.map(e => e.sd.path).concat(scene.wire ? scene.framesWire.map(e =>
                    e.sd.path) : []))
                if (res) {
                    scene.frames.forEach(frame => {
                        frame.sd.image = this.imageManager.images[frame.sd.path]
                    })
                    if (scene.wire) {
                        scene.framesWire.forEach(frameWire => {
                            frameWire.sd.image = this.imageManager.images[frameWire.sd.path]
                        })
                    }
                    scene.status = Status.BLUR_LOADED
                }
            }
        }

        // Other Scenes HD
        for (let scene of this.scenes.filter((scene, i) => scene !== targetScene && scene.status < Status.FULL_LOADED)) {
            if (lastLoadImageIndex === this.loadImageIndex) {
                const res = await this.imageManager.loadImages(scene.frames.map(e => e.hd.path).concat(scene.wire ? scene.framesWire.map(e =>
                    e.hd.path) : []))
                if (res) {
                    scene.frames.forEach(frame => {
                        frame.hd.image = this.imageManager.images[frame.hd.path]
                    })
                    if (scene.wire) {
                        scene.framesWire.forEach(frameWire => {
                            frameWire.hd.image = this.imageManager.images[frameWire.hd.path]
                        })
                    }
                    scene.status = Status.FULL_LOADED
                }
            }
        }

    }

    sceneStartedLoadeds: Scene[] = [];
    private async loadImagesFast(targetScene: Scene) {
        if (targetScene.status < Status.FULL_LOADED) {
            if (!this.sceneStartedLoadeds.find(e => e === targetScene)) {
                this.sceneStartedLoadeds.push(targetScene)

                const res = await this.imageManager.loadImagesFast(
                    targetScene.frames.map(frame => frame.hd.path)
                        .concat(!targetScene.wire ? [] : targetScene.framesWire.map(frameWire => frameWire.hd.path))
                        .concat(!targetScene.effectMap ? [] : targetScene.framesMap.map(frameMap => frameMap.hd.path))
                    ,
                    (e) => {
                        // onFrameLoaded
                        if (this.activeScene === targetScene) {
                            this.events.fastLoading.next(e.loading)
                            setTimeout(() => {
                                this.drawer.preloadImage(e.image, e.path.indexOf("wire") !== -1, e.path.indexOf("map") !== -1)
                            }, 0)
                        }
                    })
                if (res) {
                    targetScene.frames.forEach(frame => {
                        frame.hd.image = this.imageManager.images[frame.hd.path]
                    })
                    if (targetScene.wire) {
                        targetScene.framesWire.forEach(frameWire => {
                            frameWire.hd.image = this.imageManager.images[frameWire.hd.path]
                        })
                    }
                    if (targetScene.effectMap) {
                        targetScene.framesMap.forEach(frameMap => {
                            frameMap.hd.image = this.imageManager.images[frameMap.hd.path]
                        })
                    }
                    console.log(targetScene);
                    targetScene.status = Status.FULL_LOADED
                    this.events.loading.next(false);
                }
            }
        }
    }

    private async setHdFrame() {
        const frame = this.getActualFrame()
        const frameWire = this.getActualWire()
        const frameMap = this.getActualMap()
        this.wireOffset = this.getWireOffset()
        this.drawer.setWireOffset(this.wireOffset)
        const targetScene = this.activeScene
        await Promise.all([
            (async () => {
                if (!frame.hd.image) {
                    frame.hd.image = await this.imageManager.loadImage(frame.hd.path)
                }
            })(),
            (async () => {
                if (this.activeScene.wire && !frameWire.hd.image) {
                    frameWire.hd.image = await this.imageManager.loadImage(frameWire.hd.path)
                }
            })(),
            (async () => {
                if (this.activeScene.effectMap && !frameMap.hd.image) {
                    frameMap.hd.image = await this.imageManager.loadImage(frameMap.hd.path)
                }
            })()
        ])

        if (frame === this.getActualFrame()) {
            // PAINT
            this.drawer.paint(frame.hd.image as HTMLImageElement, frameWire.hd.image, frameMap.hd.image)
            if (targetScene.wire) {
                // this.glCanvas.style.opacity = "1"
                this.mouseCanvas.width = (frameWire.hd.image as HTMLImageElement).width
                this.mouseCanvas.height = (frameWire.hd.image as HTMLImageElement).height
                this.mouseCtx.drawImage(frameWire.hd.image as HTMLImageElement, 0, 0, this.mouseCanvas.width, this.mouseCanvas.height)
            } else {
                // this.glCanvas.style.opacity = "0"
            }
            this.glCanvas.style.filter = `blur(0px)`;
            this.lastActiveFrame = frame;
            this.activeFrame = frame;
            this.activeFrameWire = this.getActualWire()
            this.activeFrameMap = this.getActualMap()
        }
    }

    public async setScene(scene: number | string) {
        this.handleSize()
        const targetScene = typeof (scene) === "number" ? this.scenes[scene] : (this.scenes.find(e => e.name === scene) as Scene)
        this.events.loading.next(targetScene.status < Status.BLUR_LOADED)
        if (this.activeScene && this.activeScene.groupId !== targetScene.groupId) {
            this.setAngle(0)
        }
        if (this.activeScene !== targetScene) {
            this.activeScene = targetScene;
            this.drawer.resetImageCache()
            await this.setHdFrame()
            this.drawer.setColors(this.activeScene.wiresId.map(e => this.pois[e].color))
            if (this.loadMode === "slow") this.loadImagesSlow(targetScene)
            if (this.loadMode === "fast") this.loadImagesFast(targetScene)
        }
    }
}